(() => {
  const GLOBAL_LOCK_ATTR = 'data-theme-scroll-lock-count';
  const LOCAL_LOCK_ATTR = 'data-theme-collection-filters-lock-count';
  const GLOBAL_LOCK_CLASS = 'theme-scroll-lock-active';
  const SCROLLBAR_VAR = '--theme-scrollbar-compensation';
  const DEFAULT_CLOSE_DURATION = 360;
  const FILTER_GROUP_SELECTOR = '.collection-filters__group';
  const FILTER_GROUP_SUMMARY_SELECTOR = '.collection-filters__group-summary';
  const FILTER_GROUP_CONTENT_SELECTOR = '.collection-filters__group-content';
  const FILTER_GROUP_CLOSING_ATTR = 'data-group-closing';
  const FILTER_GROUP_KEY_ATTR = 'data-filter-group-key';
  const FILTER_QUERY_KEYS = new Set(['sort_by', 'q', 'options[prefix]', 'page']);
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');
  const ZERO_DECIMAL_CURRENCIES = new Set(['BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW', 'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF']);
  const THREE_DECIMAL_CURRENCIES = new Set(['BHD', 'IQD', 'JOD', 'KWD', 'LYD', 'OMR', 'TND']);

  const getScrollbarCompensation = () => Math.max(0, window.innerWidth - document.documentElement.clientWidth);

  const acquireGlobalScrollLock = () => {
    const root = document.documentElement;
    const body = document.body;
    const current = Number.parseInt(root.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10);

    if (current <= 0) {
      const compensation = `${getScrollbarCompensation()}px`;
      root.style.setProperty(SCROLLBAR_VAR, compensation);
      body.style.setProperty(SCROLLBAR_VAR, compensation);
      root.classList.add(GLOBAL_LOCK_CLASS);
      body.classList.add(GLOBAL_LOCK_CLASS);
    }

    root.setAttribute(GLOBAL_LOCK_ATTR, String(current + 1));
  };

  const releaseGlobalScrollLock = () => {
    const root = document.documentElement;
    const body = document.body;
    const current = Number.parseInt(root.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10);
    const next = Math.max(0, current - 1);

    if (next === 0) {
      root.classList.remove(GLOBAL_LOCK_CLASS);
      body.classList.remove(GLOBAL_LOCK_CLASS);
      root.removeAttribute(GLOBAL_LOCK_ATTR);
      root.style.removeProperty(SCROLLBAR_VAR);
      body.style.removeProperty(SCROLLBAR_VAR);
      return;
    }

    root.setAttribute(GLOBAL_LOCK_ATTR, String(next));
  };

  const FILTER_OPEN_CLASS = 'theme-collection-filter-open';

  const lockScroll = () => {
    const root = document.documentElement;
    const current = Number.parseInt(root.getAttribute(LOCAL_LOCK_ATTR) || '0', 10);

    if (current <= 0) {
      acquireGlobalScrollLock();
      root.classList.add(FILTER_OPEN_CLASS);
    }

    root.setAttribute(LOCAL_LOCK_ATTR, String(current + 1));
  };

  const unlockScroll = () => {
    const root = document.documentElement;
    const current = Number.parseInt(root.getAttribute(LOCAL_LOCK_ATTR) || '0', 10);
    const next = Math.max(0, current - 1);

    if (next === 0) {
      root.removeAttribute(LOCAL_LOCK_ATTR);
      root.classList.remove(FILTER_OPEN_CLASS);
      releaseGlobalScrollLock();
      return;
    }

    root.setAttribute(LOCAL_LOCK_ATTR, String(next));
  };

  const debounce = (fn, delay = 320) => {
    let timeoutId = 0;
    return (...args) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => fn(...args), delay);
    };
  };

  const parseDurationMs = (rawValue) => {
    if (!rawValue) return NaN;
    const value = rawValue.trim();
    if (value.endsWith('ms')) return Number.parseFloat(value);
    if (value.endsWith('s')) return Number.parseFloat(value) * 1000;
    return Number.parseFloat(value);
  };

  const getMotionDuration = (element) => {
    const style = window.getComputedStyle(element || document.documentElement);
    const duration = parseDurationMs(style.getPropertyValue('--motion-duration-normal'));
    if (Number.isFinite(duration) && duration > 0) {
      return duration;
    }
    return DEFAULT_CLOSE_DURATION;
  };

  const getCurrencyPrecision = (currencyCode = '') => {
    const normalizedCode = String(currencyCode || '').trim().toUpperCase();
    if (ZERO_DECIMAL_CURRENCIES.has(normalizedCode)) return 0;
    if (THREE_DECIMAL_CURRENCIES.has(normalizedCode)) return 3;
    return 2;
  };

  const parseLocalizedNumber = (value, locale, fallback = NaN) => {
    const raw = String(value ?? '').trim();
    if (!raw) return fallback;

    const compactValue = raw.replace(/\s+/g, '').replace(/[^\d.,-]/g, '');
    if (!compactValue) return fallback;

    const parseWithNormalizedValue = (normalized) => {
      const parsed = Number.parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : NaN;
    };

    const parseWithLocaleSeparators = () => {
      if (!locale || typeof Intl === 'undefined') return NaN;
      const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
      const groupSeparator = parts.find((part) => part.type === 'group')?.value;
      const decimalSeparator = parts.find((part) => part.type === 'decimal')?.value;

      if (!groupSeparator && !decimalSeparator) return NaN;

      let normalized = compactValue;

      if (groupSeparator) {
        normalized = normalized.split(groupSeparator).join('');
      }

      if (decimalSeparator && decimalSeparator !== '.') {
        normalized = normalized.split(decimalSeparator).join('.');
      }

      return parseWithNormalizedValue(normalized);
    };

    const parseWithSeparatorHeuristic = () => {
      const lastComma = compactValue.lastIndexOf(',');
      const lastDot = compactValue.lastIndexOf('.');
      const decimalIndex = Math.max(lastComma, lastDot);

      if (decimalIndex === -1) {
        return parseWithNormalizedValue(compactValue.replace(/[.,]/g, ''));
      }

      const integerPart = compactValue.slice(0, decimalIndex).replace(/[.,]/g, '');
      const fractionPart = compactValue.slice(decimalIndex + 1).replace(/[.,]/g, '');
      return parseWithNormalizedValue(`${integerPart}.${fractionPart}`);
    };

    const localeParsed = parseWithLocaleSeparators();
    if (Number.isFinite(localeParsed)) return localeParsed;

    const heuristicParsed = parseWithSeparatorHeuristic();
    if (Number.isFinite(heuristicParsed)) return heuristicParsed;

    return fallback;
  };

  const parseDisplayToMinorUnits = (value, currencyCode, locale, fallbackMinor = 0) => {
    const precision = getCurrencyPrecision(currencyCode);
    const unit = 10 ** precision;
    const parsedMajor = parseLocalizedNumber(value, locale, NaN);
    if (!Number.isFinite(parsedMajor)) return fallbackMinor;
    return Math.round(parsedMajor * unit);
  };

  const formatMinorUnitsForInput = (minorValue, currencyCode) => {
    const precision = getCurrencyPrecision(currencyCode);
    const unit = 10 ** precision;
    const majorValue = minorValue / unit;
    if (precision <= 0) return String(Math.round(majorValue));
    return majorValue.toFixed(precision).replace(/\.?0+$/, '');
  };

  class CollectionFacets extends HTMLElement {
    constructor() {
      super();
      this.sectionId = '';
      this.dynamicRoot = null;
      this.liveForm = null;
      this.drawerForm = null;
      this.drawer = null;
      this.desktopSortSelect = null;
      this.drawerSortSelect = null;
      this.liveSortHidden = null;
      this.priceRanges = [];
      this.drawerCloseTimer = 0;
      this.drawerCloseDuration = DEFAULT_CLOSE_DURATION;
      this.isDrawerOpen = false;
      this.viewMode = '';
      this.viewStorageKey = '';
      this.requestSeq = 0;
      this.inflightController = null;

      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handlePopstate = this.handlePopstate.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleDrawerFocusGuard = this.handleDrawerFocusGuard.bind(this);
      this.handleFilterGroupClick = this.handleFilterGroupClick.bind(this);
      this.handleFilterGroupToggle = this.handleFilterGroupToggle.bind(this);
      this.handleResize = debounce(() => this.syncAllFilterGroupHeights(), 120);
      this.debouncedLiveSubmit = debounce(() => {
        if (this.liveForm) this.requestFromForm(this.liveForm, true);
      }, 280);
      this.debouncedPriceRangeSubmit = debounce(() => {
        if (this.liveForm) this.requestFromForm(this.liveForm, true);
      }, 700);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId || this.closest('[data-section-id]')?.getAttribute('data-section-id') || '';
      this.cacheNodes();

      this.addEventListener('click', this.handleClick);
      this.addEventListener('change', this.handleChange);
      this.addEventListener('submit', this.handleSubmit);
      this.addEventListener('keydown', this.handleKeydown);
      this.addEventListener('click', this.handleFilterGroupClick, true);
      this.addEventListener('toggle', this.handleFilterGroupToggle, true);
      window.addEventListener('popstate', this.handlePopstate);
      window.addEventListener('resize', this.handleResize, { passive: true });

      this.syncSortValueAcrossForms();
      this.initializeViewMode();
      this.syncDrawerToggleState();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener('submit', this.handleSubmit);
      this.removeEventListener('keydown', this.handleKeydown);
      this.removeEventListener('click', this.handleFilterGroupClick, true);
      this.removeEventListener('toggle', this.handleFilterGroupToggle, true);
      window.removeEventListener('popstate', this.handlePopstate);
      window.removeEventListener('resize', this.handleResize);
      window.clearTimeout(this.drawerCloseTimer);
      document.removeEventListener('focusin', this.handleDrawerFocusGuard);
      this.cancelPendingDrawerOpen();
      if (this.isDrawerOpen) {
        unlockScroll();
        this.isDrawerOpen = false;
      }
    }

    cacheNodes() {
      this.dynamicRoot = this.querySelector('[data-collection-dynamic]') || this;
      this.liveForm = this.querySelector('[data-facets-live-form]');
      this.drawerForm = this.querySelector('[data-facets-drawer-form]');
      this.drawer = this.querySelector('[data-facets-drawer]');
      this.desktopSortSelect = this.querySelector('[data-collection-sort-select-desktop]');
      this.drawerSortSelect = this.querySelector('[data-facets-drawer] [data-collection-sort-select]');
      this.liveSortHidden = this.liveForm?.querySelector('[data-collection-sort-hidden]') || null;
      this.priceRanges = Array.from(this.querySelectorAll('[data-price-range]'));
      if (this.drawer) {
        this.drawerCloseDuration = getMotionDuration(this.drawer);
      }

      this.initializePriceRanges();
      this.syncAllFilterGroupHeights();
      this.syncViewButtons();
    }

    handleClick(event) {
      const openTrigger = event.target.closest('[data-facets-drawer-toggle]');
      if (openTrigger && this.contains(openTrigger)) {
        event.preventDefault();
        this.openDrawer(openTrigger);
        return;
      }

      const closeTrigger = event.target.closest('[data-facets-drawer-close], [data-facets-drawer-overlay]');
      if (closeTrigger && this.contains(closeTrigger)) {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      const link = event.target.closest('[data-facets-remove], [data-facets-clear], [data-facets-page-link]');
      if (link && this.contains(link) && link.href) {
        event.preventDefault();
        this.requestFromUrl(link.href, true);
        if (this.isDrawerOpen) this.closeDrawer();
        return;
      }

      const viewToggleButton = event.target.closest('[data-collection-view-button]');
      if (viewToggleButton && this.contains(viewToggleButton)) {
        event.preventDefault();
        const nextMode = viewToggleButton.getAttribute('data-collection-view-button');
        this.applyViewMode(nextMode);
      }
    }

    handleChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const isPriceRangeControl = target.matches('[data-price-range-min-slider], [data-price-range-max-slider], [data-price-range-min-input], [data-price-range-max-input]');
      let priceRangeRoot = target.closest('[data-price-range]');
      if (!priceRangeRoot && isPriceRangeControl) {
        priceRangeRoot = target.closest('.collection-filters__group-content')?.querySelector('[data-price-range]') || null;
      }

      if (priceRangeRoot) {
        this.syncPriceRange(priceRangeRoot, target);
      }

      if (isPriceRangeControl) {
        const ownerForm = target.closest('form');
        if (ownerForm?.hasAttribute('data-facets-live-form')) {
          this.debouncedPriceRangeSubmit();
        }
        return;
      }

      if (this.desktopSortSelect && target === this.desktopSortSelect) {
        const sortValue = this.desktopSortSelect.value;
        this.syncSortValueAcrossForms(sortValue);
        if (this.liveForm) {
          this.requestFromForm(this.liveForm, true);
        } else {
          this.requestFromCurrentQuery(sortValue, true);
        }
        return;
      }

      if (this.drawerSortSelect && target === this.drawerSortSelect) {
        this.syncSortValueAcrossForms(this.drawerSortSelect.value);
        return;
      }

      const liveForm = target.closest('[data-facets-live-form]');
      if (liveForm && this.contains(liveForm)) {
        this.syncSortValueAcrossForms();
        this.debouncedLiveSubmit();
      }
    }

    handleSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      if (form.hasAttribute('data-facets-drawer-form')) {
        event.preventDefault();
        this.syncSortValueAcrossForms(this.drawerSortSelect?.value || this.desktopSortSelect?.value || '');
        this.requestFromForm(form, true, { restoreFocus: false }).then(() => {
          this.closeDrawer();
        });
        return;
      }

      if (form.hasAttribute('data-facets-live-form')) {
        event.preventDefault();
        this.syncSortValueAcrossForms();
        this.requestFromForm(form, true);
      }
    }

    handlePopstate() {
      const query = window.location.search.replace(/^\?/, '');
      this.requestSection(query, false);
    }

    handleKeydown(event) {
      if (!this.isDrawerOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      if (event.key === 'Tab') {
        this.trapFocus(event);
      }
    }

    getDrawerFocusScope() {
      if (!this.drawer) return null;
      return this.drawer.querySelector('.collection-filters-drawer__panel') || this.drawer;
    }

    getDrawerFocusables() {
      const scope = this.getDrawerFocusScope();
      if (!scope) return [];
      return Array.from(scope.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
        if (element.closest('[hidden]')) return false;
        // Visible elements have layout boxes; the active element always counts.
        return element === document.activeElement || element.getClientRects().length > 0;
      });
    }

    trapFocus(event) {
      const scope = this.getDrawerFocusScope();
      if (!scope) return;

      const focusables = this.getDrawerFocusables();
      if (!focusables.length) {
        event.preventDefault();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      // Focus escaped the drawer (or never entered) — pull it back in.
      if (!scope.contains(active)) {
        event.preventDefault();
        this.focusElement(first);
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        this.focusElement(last);
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        this.focusElement(first);
      }
    }

    focusElement(element) {
      if (!(element instanceof HTMLElement)) return;
      try {
        element.focus({ preventScroll: true });
      } catch (_error) {
        element.focus();
      }
    }

    // Defense-in-depth: if focus ever lands outside the open drawer (e.g. a
    // programmatic focus move, or a Tab that bypasses the component-scoped
    // keydown listener), pull it back into the drawer panel.
    handleDrawerFocusGuard(event) {
      if (!this.isDrawerOpen) return;
      const scope = this.getDrawerFocusScope();
      if (!scope) return;
      const target = event.target;
      if (scope.contains(target) || target === scope) return;
      const focusables = this.getDrawerFocusables();
      this.focusElement(focusables[0] || scope);
    }

    syncSortValueAcrossForms(explicitValue) {
      const value = explicitValue !== undefined
        ? explicitValue
        : (this.desktopSortSelect?.value || this.drawerSortSelect?.value || this.liveSortHidden?.value || '');

      if (this.desktopSortSelect && this.desktopSortSelect.value !== value) {
        this.desktopSortSelect.value = value;
      }

      if (this.drawerSortSelect && this.drawerSortSelect.value !== value) {
        this.drawerSortSelect.value = value;
      }

      if (this.liveSortHidden && this.liveSortHidden.value !== value) {
        this.liveSortHidden.value = value;
      }
    }

    initializePriceRanges() {
      this.priceRanges.forEach((rangeRoot) => {
        if (!(rangeRoot instanceof HTMLElement) || rangeRoot.dataset.rangeBound === 'true') return;

        const minSlider = rangeRoot.querySelector('[data-price-range-min-slider]');
        const maxSlider = rangeRoot.querySelector('[data-price-range-max-slider]');
        const minInput = rangeRoot.parentElement?.querySelector('[data-price-range-min-input]');
        const maxInput = rangeRoot.parentElement?.querySelector('[data-price-range-max-input]');
        if (!minSlider || !maxSlider || !minInput || !maxInput) return;

        rangeRoot.dataset.rangeBound = 'true';
        this.syncPriceRange(rangeRoot);

        const onSliderInput = (event) => {
          this.syncPriceRange(rangeRoot, event.currentTarget);
        };

        const onInputTyping = (event) => {
          this.syncPriceRange(rangeRoot, event.currentTarget);
        };

        const startDragging = (handle) => {
          rangeRoot.dataset.dragging = handle;
        };

        const stopDragging = () => {
          delete rangeRoot.dataset.dragging;
        };

        minSlider.addEventListener('input', onSliderInput);
        maxSlider.addEventListener('input', onSliderInput);
        minInput.addEventListener('input', onInputTyping);
        maxInput.addEventListener('input', onInputTyping);

        minSlider.addEventListener('pointerdown', () => startDragging('min'));
        maxSlider.addEventListener('pointerdown', () => startDragging('max'));
        minSlider.addEventListener('mousedown', () => startDragging('min'));
        maxSlider.addEventListener('mousedown', () => startDragging('max'));
        minSlider.addEventListener('touchstart', () => startDragging('min'), { passive: true });
        maxSlider.addEventListener('touchstart', () => startDragging('max'), { passive: true });

        ['pointerup', 'pointercancel', 'mouseup', 'touchend', 'blur', 'change'].forEach((eventName) => {
          minSlider.addEventListener(eventName, stopDragging);
          maxSlider.addEventListener(eventName, stopDragging);
        });
      });
    }

    syncFilterGroupHeight(group) {
      if (!(group instanceof HTMLDetailsElement)) return;
      const content = group.querySelector(FILTER_GROUP_CONTENT_SELECTOR);
      if (!(content instanceof HTMLElement)) return;
      const measuredHeight = Math.ceil(content.scrollHeight + 8);
      group.style.setProperty('--filter-group-content-height', `${measuredHeight}px`);
    }

    syncAllFilterGroupHeights() {
      this.querySelectorAll(FILTER_GROUP_SELECTOR).forEach((group) => {
        this.syncFilterGroupHeight(group);
      });
    }

    closeFilterGroup(group) {
      if (!(group instanceof HTMLDetailsElement) || !group.open) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        group.removeAttribute(FILTER_GROUP_CLOSING_ATTR);
        group.removeAttribute('open');
        return;
      }
      if (group.hasAttribute(FILTER_GROUP_CLOSING_ATTR)) return;

      const content = group.querySelector(FILTER_GROUP_CONTENT_SELECTOR);
      if (!(content instanceof HTMLElement)) {
        group.removeAttribute('open');
        return;
      }

      this.syncFilterGroupHeight(group);
      group.setAttribute(FILTER_GROUP_CLOSING_ATTR, 'true');

      let finished = false;
      const cleanup = () => {
        if (finished) return;
        finished = true;
        content.removeEventListener('transitionend', onTransitionEnd);
        group.removeAttribute(FILTER_GROUP_CLOSING_ATTR);
        group.removeAttribute('open');
        this.syncFilterGroupHeight(group);
      };

      const onTransitionEnd = (event) => {
        if (event.target !== content || event.propertyName !== 'max-height') return;
        cleanup();
      };

      content.addEventListener('transitionend', onTransitionEnd);
      window.setTimeout(cleanup, Math.max(220, getMotionDuration(group) + 120));
    }

    handleFilterGroupClick(event) {
      const summary = event.target.closest(FILTER_GROUP_SUMMARY_SELECTOR);
      if (!(summary instanceof HTMLElement) || !this.contains(summary)) return;

      const group = summary.closest(FILTER_GROUP_SELECTOR);
      if (!(group instanceof HTMLDetailsElement)) return;
      if (!group.open || group.hasAttribute(FILTER_GROUP_CLOSING_ATTR)) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      event.preventDefault();
      this.closeFilterGroup(group);
    }

    handleFilterGroupToggle(event) {
      const group = event.target;
      if (!(group instanceof HTMLDetailsElement) || !group.matches(FILTER_GROUP_SELECTOR)) return;
      if (!this.contains(group)) return;

      if (group.open) {
        group.removeAttribute(FILTER_GROUP_CLOSING_ATTR);
      }
      this.syncFilterGroupHeight(group);
      if (group.open) {
        window.requestAnimationFrame(() => this.syncFilterGroupHeight(group));
      }
    }

    initializeViewMode() {
      const viewToggleEnabled = this.dataset.enableViewToggle === 'true';
      const validModes = ['3', '4', '5'];
      const rawDefault = this.dataset.viewDefault || '4';
      const defaultMode = validModes.includes(rawDefault) ? rawDefault : '4';

      if (!viewToggleEnabled) {
        this.viewMode = '';
        this.style.removeProperty('--collection-view-cols-desktop');
        return;
      }

      this.viewStorageKey = `theme-collection-view-${this.sectionId || 'default'}`;
      let persistedMode = '';

      try {
        persistedMode = window.localStorage.getItem(this.viewStorageKey) || '';
      } catch (_error) {
        persistedMode = '';
      }

      const resolvedMode = validModes.includes(persistedMode) ? persistedMode : defaultMode;
      this.applyViewMode(resolvedMode, { persist: false });
    }

    syncViewButtons(activeMode) {
      const mode = activeMode || this.viewMode;
      const buttons = Array.from(this.querySelectorAll('[data-collection-view-button]'));
      if (!buttons.length) return;

      let activeButton = null;
      buttons.forEach((button) => {
        const buttonMode = button.getAttribute('data-collection-view-button');
        const isActive = buttonMode === mode;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        if (isActive) activeButton = button;
      });

      this.positionViewIndicator(activeButton);
    }

    positionViewIndicator(activeButton) {
      const toggle = this.querySelector('[data-collection-view-toggle]');
      if (!toggle) return;
      const indicator = toggle.querySelector('.collection-main__view-toggle-indicator');
      if (!indicator) return;

      if (!activeButton || activeButton.offsetWidth <= 0) {
        indicator.style.opacity = '0';
        return;
      }

      const x = activeButton.offsetLeft;
      const w = activeButton.offsetWidth;
      indicator.style.transform = `translate3d(${x}px, 0, 0)`;
      indicator.style.width = `${w}px`;
      indicator.style.opacity = '1';
    }

    applyViewMode(mode, { persist = true } = {}) {
      if (mode !== '3' && mode !== '4' && mode !== '5') return;

      const grid = this.querySelector('.collection-main__products');
      const shouldAnimate = persist && grid && this.viewMode && this.viewMode !== mode;

      this.syncViewButtons(mode);

      if (shouldAnimate) {
        grid.classList.add('view-switching');
        setTimeout(() => {
          this.viewMode = mode;
          this.style.setProperty('--collection-view-cols-desktop', mode);
          requestAnimationFrame(() => grid.classList.remove('view-switching'));
        }, 300);
      } else {
        this.viewMode = mode;
        this.style.setProperty('--collection-view-cols-desktop', mode);
      }

      if (!persist || !this.viewStorageKey) return;
      try {
        window.localStorage.setItem(this.viewStorageKey, mode);
      } catch (_error) {
        // Intentionally ignored: storage is a progressive enhancement only.
      }
    }

    syncPriceRange(rangeRoot, sourceElement = null) {
      if (!(rangeRoot instanceof HTMLElement)) return;

      const minSlider = rangeRoot.querySelector('[data-price-range-min-slider]');
      const maxSlider = rangeRoot.querySelector('[data-price-range-max-slider]');
      const minInput = rangeRoot.parentElement?.querySelector('[data-price-range-min-input]');
      const maxInput = rangeRoot.parentElement?.querySelector('[data-price-range-max-input]');
      if (!minSlider || !maxSlider || !minInput || !maxInput) return;

      const currencyCode = (rangeRoot.dataset.currencyCode || '').toUpperCase();
      const locale = rangeRoot.dataset.locale || document.documentElement.lang || undefined;
      const minLimit = Number.parseInt(rangeRoot.dataset.minorMin || '0', 10) || 0;
      const maxLimit = Number.parseInt(rangeRoot.dataset.minorMax || '0', 10) || 0;

      const parseSliderValue = (value, fallback) => {
        const parsed = Number.parseInt(String(value ?? ''), 10);
        return Number.isFinite(parsed) ? parsed : fallback;
      };

      let minValue = parseDisplayToMinorUnits(minInput.value, currencyCode, locale, minLimit);
      let maxValue = parseDisplayToMinorUnits(maxInput.value, currencyCode, locale, maxLimit);
      let minSliderValue = parseSliderValue(minSlider.value, minLimit);
      let maxSliderValue = parseSliderValue(maxSlider.value, maxLimit);
      const hadMinInputValue = String(minInput.value ?? '').trim() !== '';
      const hadMaxInputValue = String(maxInput.value ?? '').trim() !== '';

      if (sourceElement === minSlider) {
        minValue = minSliderValue;
      } else if (sourceElement === maxSlider) {
        maxValue = maxSliderValue;
      } else if (sourceElement === minInput) {
        minSliderValue = minValue;
      } else if (sourceElement === maxInput) {
        maxSliderValue = maxValue;
      } else {
        minValue = minSliderValue;
        maxValue = maxSliderValue;
      }

      minValue = Math.max(minLimit, Math.min(minValue, maxLimit));
      maxValue = Math.max(minLimit, Math.min(maxValue, maxLimit));

      if (minValue > maxValue) {
        if (sourceElement === minSlider || sourceElement === minInput) {
          maxValue = minValue;
        } else {
          minValue = maxValue;
        }
      }

      minSlider.value = String(minValue);
      maxSlider.value = String(maxValue);

      const sourceIsInteractive = sourceElement === minSlider
        || sourceElement === maxSlider
        || sourceElement === minInput
        || sourceElement === maxInput;

      if (sourceIsInteractive) {
        minInput.value = formatMinorUnitsForInput(minValue, currencyCode);
        maxInput.value = formatMinorUnitsForInput(maxValue, currencyCode);
      } else {
        minInput.value = hadMinInputValue ? formatMinorUnitsForInput(minValue, currencyCode) : '';
        maxInput.value = hadMaxInputValue ? formatMinorUnitsForInput(maxValue, currencyCode) : '';
      }

      const safeSpan = Math.max(1, maxLimit - minLimit);
      const start = ((minValue - minLimit) / safeSpan) * 100;
      const end = ((maxValue - minLimit) / safeSpan) * 100;
      rangeRoot.style.setProperty('--range-start', `${Math.max(0, Math.min(100, start)).toFixed(2)}%`);
      rangeRoot.style.setProperty('--range-end', `${Math.max(0, Math.min(100, end)).toFixed(2)}%`);
    }

    serializeForm(form) {
      const params = new URLSearchParams(new FormData(form));
      const pairs = Array.from(params.entries());
      pairs.forEach(([key, value]) => {
        if (value === '' || value == null) {
          params.delete(key);
        }
      });
      return params.toString();
    }

    requestFromForm(form, pushState, options) {
      if (!(form instanceof HTMLFormElement)) return Promise.resolve();
      const query = this.serializeForm(form);
      return this.requestSection(query, pushState, options);
    }

    requestFromUrl(url, pushState, options) {
      const parsed = new URL(url, window.location.origin);
      return this.requestSection(parsed.searchParams.toString(), pushState, options);
    }

    requestFromCurrentQuery(sortBy, pushState, options) {
      const params = new URLSearchParams(window.location.search);

      if (sortBy) {
        params.set('sort_by', sortBy);
      } else {
        params.delete('sort_by');
      }

      return this.requestSection(params.toString(), pushState, options);
    }

    captureUiState() {
      const state = { openGroups: new Set(), focus: null };

      this.querySelectorAll(`${FILTER_GROUP_SELECTOR}[${FILTER_GROUP_KEY_ATTR}]`).forEach((group) => {
        if (!(group instanceof HTMLDetailsElement) || !group.open) return;
        if (group.hasAttribute(FILTER_GROUP_CLOSING_ATTR)) return;
        const key = group.getAttribute(FILTER_GROUP_KEY_ATTR);
        if (key) state.openGroups.add(key);
      });

      const active = document.activeElement;
      if (active instanceof HTMLElement && this.contains(active) && active !== document.body) {
        if (active instanceof HTMLInputElement && active.name) {
          state.focus = { kind: 'input', name: active.name, value: active.value };
        } else if (active instanceof HTMLSelectElement && active.name) {
          state.focus = { kind: 'select', name: active.name };
        } else if (active.matches(FILTER_GROUP_SUMMARY_SELECTOR)) {
          const key = active.closest(FILTER_GROUP_SELECTOR)?.getAttribute(FILTER_GROUP_KEY_ATTR) || '';
          if (key) state.focus = { kind: 'summary', key };
        }
      }

      return state;
    }

    restoreUiState(state, { restoreFocus = true } = {}) {
      if (!state) return;

      this.querySelectorAll(`${FILTER_GROUP_SELECTOR}[${FILTER_GROUP_KEY_ATTR}]`).forEach((group) => {
        if (!(group instanceof HTMLDetailsElement)) return;
        const key = group.getAttribute(FILTER_GROUP_KEY_ATTR);
        if (key && state.openGroups.has(key) && !group.open) {
          group.open = true;
        }
      });

      if (!restoreFocus || !state.focus) return;

      const escape = (value) => (typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&'));
      let nextFocus = null;

      if (state.focus.kind === 'input' && state.focus.name) {
        const nameSel = escape(state.focus.name);
        const valueSel = escape(state.focus.value || '');
        nextFocus = this.querySelector(`input[name="${nameSel}"][value="${valueSel}"]`)
          || this.querySelector(`input[name="${nameSel}"]`);
      } else if (state.focus.kind === 'select' && state.focus.name) {
        nextFocus = this.querySelector(`select[name="${escape(state.focus.name)}"]`);
      } else if (state.focus.kind === 'summary' && state.focus.key) {
        nextFocus = this.querySelector(`${FILTER_GROUP_SELECTOR}[${FILTER_GROUP_KEY_ATTR}="${escape(state.focus.key)}"] ${FILTER_GROUP_SUMMARY_SELECTOR}`);
      }

      if (nextFocus instanceof HTMLElement) {
        try { nextFocus.focus({ preventScroll: true }); } catch (_error) { nextFocus.focus(); }
      }
    }

    buildMergedQueryParams(query, { includeSectionId = false } = {}) {
      const merged = new URLSearchParams(window.location.search);
      merged.delete('section_id');

      const keysToClear = [];
      merged.forEach((_, key) => {
        if (key.startsWith('filter.') || FILTER_QUERY_KEYS.has(key)) {
          keysToClear.push(key);
        }
      });
      keysToClear.forEach((key) => merged.delete(key));

      const queryParams = new URLSearchParams(query || '');
      queryParams.forEach((value, key) => {
        if (value === '' || value == null) return;
        if (!key.startsWith('filter.')) {
          merged.delete(key);
        }
        merged.append(key, value);
      });

      if (includeSectionId) {
        merged.set('section_id', this.sectionId);
      }

      return merged;
    }

    async requestSection(query, pushState, { restoreFocus = true } = {}) {
      if (!this.sectionId || !this.dynamicRoot) return;

      const requestParams = this.buildMergedQueryParams(query, { includeSectionId: true });
      const requestQuery = requestParams.toString();
      const requestUrl = `${window.location.pathname}${requestQuery ? `?${requestQuery}` : ''}`;

      const requestId = ++this.requestSeq;
      if (this.inflightController) this.inflightController.abort();
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      this.inflightController = controller;

      const uiState = this.captureUiState();

      try {
        this.setLoading(true);

        const response = await fetch(requestUrl, {
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest'
          },
          signal: controller ? controller.signal : undefined
        });

        if (requestId !== this.requestSeq) return;
        if (!response.ok) throw new Error(`Request failed (${response.status})`);

        const responseHtml = await response.text();
        if (requestId !== this.requestSeq) return;

        const parsed = new DOMParser().parseFromString(responseHtml, 'text/html');
        const nextSection = parsed.getElementById(`shopify-section-${this.sectionId}`);
        const nextDynamic = nextSection?.querySelector('[data-collection-dynamic]') || parsed.querySelector('[data-collection-dynamic]');

        if (!nextDynamic) throw new Error('Dynamic collection fragment missing');

        this.dynamicRoot.innerHTML = nextDynamic.innerHTML;
        this.cacheNodes();
        this.syncSortValueAcrossForms();
        if (this.dataset.enableViewToggle === 'true') {
          const fallbackMode = this.dataset.viewDefault === '5' ? '5' : '3';
          this.applyViewMode(this.viewMode || fallbackMode, { persist: false });
        }
        this.syncDrawerToggleState();
        this.restoreUiState(uiState, { restoreFocus });
        this.syncAllFilterGroupHeights();

        if (pushState) {
          const historyParams = this.buildMergedQueryParams(query);
          const historyQuery = historyParams.toString();
          const nextUrl = `${window.location.pathname}${historyQuery ? `?${historyQuery}` : ''}`;
          window.history.pushState({ query: historyQuery }, '', nextUrl);
        }

        this.dispatchEvent(new CustomEvent('collection:updated', { bubbles: true }));
      } catch (error) {
        if (error && (error.name === 'AbortError' || requestId !== this.requestSeq)) return;
        const fallbackParams = this.buildMergedQueryParams(query);
        const fallbackQuery = fallbackParams.toString();
        const fallbackUrl = `${window.location.pathname}${fallbackQuery ? `?${fallbackQuery}` : ''}`;
        window.location.assign(fallbackUrl);
      } finally {
        if (requestId === this.requestSeq) {
          this.inflightController = null;
          this.setLoading(false);
        }
      }
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', Boolean(isLoading));
      this.togglePriceRangeInteractivity(Boolean(isLoading));
    }

    togglePriceRangeInteractivity(isLoading) {
      const controls = this.querySelectorAll(
        '[data-price-range-min-slider], [data-price-range-max-slider], [data-price-range-min-input], [data-price-range-max-input]'
      );

      controls.forEach((control) => {
        if (!(control instanceof HTMLInputElement)) return;
        control.disabled = isLoading;
      });
    }

    syncDrawerToggleState() {
      const expanded = this.isDrawerOpen ? 'true' : 'false';
      this.querySelectorAll('[data-facets-drawer-toggle]').forEach((button) => {
        button.setAttribute('aria-expanded', expanded);
      });

      if (this.drawer) {
        this.drawer.setAttribute('aria-hidden', this.isDrawerOpen ? 'false' : 'true');
      }
    }

    openDrawer(trigger) {
      if (!this.drawer || this.isDrawerOpen) return;

      // Remember what to return focus to when the drawer closes.
      this.drawerTrigger = (trigger instanceof HTMLElement)
        ? trigger
        : (document.activeElement instanceof HTMLElement ? document.activeElement : null);

      window.clearTimeout(this.drawerCloseTimer);
      this.cancelPendingDrawerOpen();
      this.drawer.hidden = false;
      this.isDrawerOpen = true;
      this.drawer.classList.remove('is-closing', 'is-open');
      this.drawer.classList.add('is-opening');

      lockScroll();
      document.addEventListener('focusin', this.handleDrawerFocusGuard);
      this.syncDrawerToggleState();
      const panel = this.drawer.querySelector('.collection-filters-drawer__panel');
      if (panel instanceof HTMLElement) void panel.offsetHeight;
      void this.drawer.offsetHeight;
      this.drawer.classList.remove('is-opening', 'is-closing');
      this.drawer.classList.add('is-open');

      // Move focus into the drawer so keyboard navigation is trapped within it.
      window.requestAnimationFrame(() => {
        this.syncAllFilterGroupHeights();
        const focusables = this.getDrawerFocusables();
        this.focusElement(focusables[0] || panel);
        window.requestAnimationFrame(() => {
          this.syncAllFilterGroupHeights();
        });
      });
    }

    closeDrawer() {
      if (!this.drawer || !this.isDrawerOpen) return;

      this.cancelPendingDrawerOpen();
      window.clearTimeout(this.drawerCloseTimer);
      this.isDrawerOpen = false;
      this.drawer.classList.remove('is-open');
      this.drawer.classList.remove('is-opening');
      this.drawer.classList.add('is-closing');
      this.syncDrawerToggleState();
      const finishClose = () => {
        if (!this.drawer) return;
        this.drawer.classList.remove('is-closing', 'is-opening');
        this.drawer.hidden = true;
      };

      if (this.drawerCloseDuration <= 0) {
        finishClose();
      } else {
        this.drawerCloseTimer = window.setTimeout(finishClose, this.drawerCloseDuration);
      }

      unlockScroll();
      document.removeEventListener('focusin', this.handleDrawerFocusGuard);

      // Return focus to the control that opened the drawer.
      if (this.drawerTrigger && this.drawerTrigger.isConnected) {
        this.focusElement(this.drawerTrigger);
      }
      this.drawerTrigger = null;
    }

    cancelPendingDrawerOpen() {
      // Open commit is synchronous (forced reflow + class swap), so nothing pending.
    }
  }

  if (!customElements.get('collection-facets')) {
    customElements.define('collection-facets', CollectionFacets);
  }
})();
